import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom"; // ✅ ADDED
import { 
  Building2, Factory, Zap, Cpu, Cloud, 
  Monitor, Smartphone, Gauge, Play, ArrowRight,Shield,
  Bell, 
  Mail, 
  History, 
  BarChart3
} from "lucide-react";

import "./Energy.css";
import dashboardImg from "../../photo/dashboard.png";

// 🌟 Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

export default function EnergyManagement() {

  const navigate = useNavigate(); // ✅ ADDED

  const [activeIndex, setActiveIndex] = useState(0);


  const toggleFAQ = (index) => {
  setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
 
  {
    question: "What does an energy management system do?",
    answer:
      "An energy management system (EMS) helps monitor, analyze, and control energy usage across facilities. It provides real-time visibility into consumption patterns, enabling better decisions to reduce waste and improve efficiency."
  },
  {
    question: "Which businesses benefit from energy monitoring?",
    answer:
      "Energy monitoring is useful for all types of organizations, including factories, offices, commercial buildings, and campuses. Any setup looking to reduce costs and optimize energy usage can benefit from an EMS."
  },
  {
    question: "How does an EMS support sustainability goals?",
    answer:
      "An EMS helps track energy consumption and identify inefficiencies, allowing organizations to reduce carbon emissions and improve overall sustainability performance."
  },
  {
    question: "What are the key advantages of using an EMS?",
    answer:
      "Key benefits include reduced energy costs, improved operational efficiency, real-time monitoring, predictive maintenance, and better decision-making through data insights."
  },
  {
    question: "Can an EMS provide real-time alerts?",
    answer:
      "Yes, most EMS platforms offer real-time alerts for unusual energy usage, equipment faults, or threshold breaches, helping teams take immediate action and prevent losses."
  },
  {
    question: "Is an EMS scalable for multiple locations?",
    answer:
      "Yes, an EMS can be scaled to monitor multiple sites from a single dashboard, providing centralized control and visibility across all locations."
  }
];






  return (
    <div className="energy-page energy-portal-shell">
      
      {/* 🧭 NAVIGATION */}
      <nav className="sub-nav">
        <div className="container flex-between">
          <div className="logo-text">POWER<span>SENSE</span></div>
          
        </div>
      </nav>

      {/* 🚀 PAGE 1: HERO SECTION */}
      <section className="portal-section hero-light">
        <div className="container hero-grid">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={containerVariants}
            className="hero-left"
          >
            <motion.span variants={fadeInUp} className="chip-terracotta">
              ENERGY MANAGEMENT SOLUTION
            </motion.span>

            <motion.h1 variants={fadeInUp} className="zoho-hero-title">
              Empower your energy <br />
              and <span className="text-gradient">sustainability</span> journey
            </motion.h1>

            <motion.p variants={fadeInUp} className="zoho-hero-desc">
              Monitor voltage, power factor, and consumption in real-time. 
              Reduce industrial electricity bills by up to 30% with intelligent load balancing.
            </motion.p>

            <motion.div variants={fadeInUp} className="hero-btns">

              {/* ✅ UPDATED BUTTON (ONLY CHANGE) */}
              <button 
                className="zoho-btn"
                onClick={() => navigate("/enquiry")}
              >
                Get Started <ArrowRight size={18} />
              </button>

            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hero-right"
          >
            <div className="hero-image-card">
              <img src={dashboardImg} alt="Dashboard" className="main-img" />
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 4 }} className="floating-card one">⚡ 156 KW</motion.div>
              <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 5, delay: 0.5 }} className="floating-card two">📊 Live Data</motion.div>
              <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 3 }} className="floating-card three">🔋 98% Efficiency</motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      

      {/* 📊 PAGE 2: FLOW DIAGRAM */}
      <section className="portal-section flow-section">
        <div className="flow-canvas-wrapper">
          <div className="container text-center">
            <h2 className="flow-header">Intelligence Pipeline Architecture</h2>
            <p className="flow-subtext">Real-time telemetry from field hardware to secure enterprise cloud</p>
            
            <div className="architecture-horizontal">
              
              <div className="master-svg-container">
                <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="master-svg">
                  <defs>
                    <filter id="glow-line">
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  <path d="M 50 40 L 150 40 Q 200 40 250 100 L 950 100" className="path-track-full" />
                  <path d="M 50 100 L 950 100" className="path-track-full" />
                  <path d="M 50 160 L 150 160 Q 200 160 250 100 L 950 100" className="path-track-full" />

                  {[40, 100, 160].map((startY, i) => (
                    <motion.path
                      key={i}
                      d={`M 50 ${startY} L 150 ${startY} ${startY === 100 ? "" : "Q 200 " + startY} 250 100 L 950 100`}
                      className="path-pulse-full"
                      initial={{ pathLength: 0.1, pathOffset: 0 }}
                      animate={{ pathOffset: 1 }}
                      transition={{
                        duration: 4,
                        ease: "linear",
                        repeat: Infinity,
                        delay: i * 1.2
                      }}
                    />
                  ))}
                </svg>
              </div>

              <div className="node-group">
                <IconPod Icon={Building2} label="Buildings" color="blue-icon" />
                <IconPod Icon={Factory} label="Factories" color="orange-icon" />
                <IconPod Icon={Zap} label="Substations" color="purple-icon" />
              </div>

              <div className="node-single node-z">
                <div className="pod-meter meter-accent">
                  <Gauge size={32} />
                  <span>Smart Meters</span>
                </div>
              </div>

              <div className="node-single node-z">
                <div className="protocol-tag">MODBUS / RS485</div>
                <div className="pod-control gateway-accent">
                  <Cpu size={40} />
                  <span>Edge Gateway</span>
                </div>
              </div>

              <div className="output-row-glass node-z">
                <div className="pod-cloud cloud-accent">
                  <Cloud size={40} />
                  <span>Secure Cloud</span>
                </div>
                <div className="pod-output-group">
                  <div className="pod-output web-accent"><Monitor size={20} /><span>Web Dash</span></div>
                  <div className="pod-output app-accent"><Smartphone size={20} /><span>Mobile App</span></div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* 🛠️ ADD THE NEW HARDWARE PAGE HERE */}
{/* 🛠️ MODULE 0: HARDWARE LAYER */}
      <section className="portal-section bg-white">
        <div className="container">
          {/* Enhanced Master Title Block */}
          <div className="section-title-container text-center">
            <span className="chip-blue">INDUSTRIAL GRADE HARDWARE</span>
            <h2 className="master-display-title">
              Precision Engineered <span className="accent-blue">Edge Connectivity</span>
            </h2>
            <p className="master-sub-p mx-auto">
              Our hardware ecosystem is designed for the most demanding industrial environments, 
              ensuring seamless integration and millisecond-accurate data acquisition.
            </p>
          </div>

          <div className="hardware-grid-pro">
            {/* Card 1: The Sensor */}
            <div className="hw-card">
              <div className="hw-icon-box"><Zap size={32} /></div>
              <h3>High-Precision CTs</h3>
              <p>Non-invasive current transformers that clip onto main lines, providing total power transparency without interruption.</p>
              <span className="spec-tag">0.5% Accuracy Class</span>
            </div>

            {/* Card 2: The Gateway */}
            <div className="hw-card">
              <div className="hw-icon-box"><Cpu size={32} /></div>
              <h3>Edge Gateway v2.0</h3>
              <p>Robust edge computing hubs powered by high-speed microcontrollers to process complex harmonics and voltage spikes locally.</p>
              <span className="spec-tag">ESP8266 / Pi Compatible</span>
            </div>

            {/* Card 3: Protocol */}
            <div className="hw-card">
              <div className="hw-icon-box"><Shield size={32} /></div>
              <h3>Industrial Protocol</h3>
              <p>Built-in support for MODBUS RTU and RS485 standards, ensuring noise-free, long-distance data transmission across factory floors.</p>
              <span className="spec-tag">RS485 / MQTT</span>
            </div>
          </div>
        </div>
      </section>
      {/* --- 📟 MODULE 1: REAL-TIME DATA --- */}
{/* --- 📟 MODULE 1: REAL-TIME DATA --- */}
      <section className="portal-section bg-fair-sage">
        <div className="container grid-2-large">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-content">
            <span className="chip-purple">LIVE TELEMETRY</span>
            <h2 className="master-display-title"> 
              Real-time Monitoring at <span className="accent-purple">60s Intervals</span>
            </h2>
            <p className="master-sub-p">
              Step into the forefront of energy intelligence. Seamlessly gather, analyze, and leverage 
              millisecond-level telemetry from field sensors for dynamic, real-time grid monitoring.
            </p>
            <div className="feature-pill-group">
              <span className="f-pill">Voltage Pulse</span>
              <span className="f-pill">Ampere Flow</span>
              <span className="f-pill">Harmonics</span>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="visual-content">
            <div className="zoho-mockup-card large-mockup glass-morph">
              <img src={require("../../photo/realtime_data.png")} alt="Realtime Data" className="mockup-img" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 📈 MODULE 2: ANALYTICS --- */}
      <section className="portal-section analytics-section">
        <div className="container grid-2-large grid-2-reverse">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-content">
            <span className="chip-blue">POWER ANALYTICS</span>
            <h2 className="master-display-title"> 
              Interactive <span className="accent-blue">Plots & Utilization</span>
            </h2>
            <p className="master-sub-p">
              Track your consumption patterns from any location. Visualize load profiles with 
              high-definition Area and Line charts to identify inefficiencies and promote granular energy saving.
            </p>
            <div className="feature-pill-group">
              <span className="f-pill">Demand Peaks</span>
              <span className="f-pill">PF Correction</span>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="visual-content">
            <div className="zoho-mockup-card large-mockup shadow-lg">
              <img src={require("../../photo/energy_plots.png")} alt="Energy Plots" className="mockup-img" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 🚨 MODULE 3: MULTI-CHANNEL ALERTS --- */}
      <section className="portal-section alert-section">
        <div className="container grid-2-large">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-content">
            <span className="chip-red">SMART WATCHDOG</span>
            <h2 className="master-display-title"> 
              Instant <span className="accent-red">Watchdog Alerts</span>
            </h2>
            <p className="master-sub-p">
              Stay updated on energy usage deviation even while away. Our intelligent watchdog 
              monitors demand limits and sends proactive alerts via WhatsApp, Mail, and SMS.
            </p>
            <div className="alert-badge-row">
              <div className="alert-item-icon"><Mail size={20}/></div>
              <div className="alert-item-icon"><Bell size={20}/></div>
              <div className="whatsapp-icon">WA</div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="visual-content">
            <div className="zoho-mockup-card large-mockup alert-card-bg">
              <img src={require("../../photo/alerts_screen.png")} alt="Alerts" className="mockup-img" />
              <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 2 }} className="alert-pulse-dot" />
            </div>
          </motion.div>
        </div>
      </section>




      {/* --- 📊 MODULE 4: ENERGY CONSUMPTION --- */}
{/* --- 🔋 MODULE 4: ENERGY CONSUMPTION & SOURCES --- */}
      {/* --- 🔋 MODULE 4: ENERGY CONSUMPTION & SOURCES --- */}
{/* --- 🔋 MODULE 4: ENERGY CONSUMPTION & SOURCES --- */}
      <section className="portal-section metering-section">
        <div className="container grid-2-ultra">
          
          <motion.div className="text-content">
            <span className="chip-purple-glow">ENERGY METERING AND ACCOUNTING</span>
            <h2 className="master-display-title"> 
              Track your energy <span className="accent-purple">consumption from <br/> any location</span>
            </h2>
            <p className="master-sub-p">
              Efficiently manage your industrial energy patterns by tracking usage at a granular level. 
            </p>
            <div className="feature-pill-group">
              <span className="f-pill-dark">Solar Analytics</span>
              <span className="f-pill-dark">Grid Importing</span>
              <span className="f-pill-dark">Total Load Sync</span>
            </div>
          </motion.div>
          
          <div className="visual-content-ultra">
            <div className="stacked-portal-v3">
              <div className="portal-base-card">
                <img src={require("../../photo/meter.png")} alt="Base" />
              </div>
              <motion.div className="portal-pop pop-top-left">
                <img src={require("../../photo/energy_plots.png")} alt="Top Left" />
              </motion.div>
              <motion.div className="portal-pop pop-top-right">
                <img src={require("../../photo/energy.png")} alt="Top Right" />
              </motion.div>
              <motion.div className="portal-pop pop-bottom-right">
                <img src={require("../../photo/power.png")} alt="Bottom" />
              </motion.div>
            </div>
          </div>

        </div>
      </section>



      {/* --- 💰 MODULE 5: ENERGY CONSUMPTION & BILLING --- */}
      <section className="portal-section billing-section">
        <div className="container grid-2-large">
          <motion.div 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }} 
            variants={fadeInUp} 
            className="text-content"
          >
            <span className="chip-green">COST OPTIMIZATION</span>
            <h2 className="master-display-title"> 
              Estimate & Manage <span className="accent-green">Monthly Bills</span>
            </h2>
            <p className="master-sub-p">
              Take control of your utility expenses. Track real-time tariff calculations and 
              receive an automated breakdown of current usage across individual blocks to 
              eliminate surprise electricity costs.
            </p>
            
            <div className="billing-stats-row">
              <div className="stat-pill">
                <span className="stat-label">Estimated Bill</span>
                <span className="stat-value text-green">₹ 42,450.00</span>
              </div>
              <div className="stat-pill">
                <span className="stat-label">Block-wise Split</span>
                <span className="stat-value">3 Active Units</span>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 50 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8 }} 
            className="visual-content"
          >
            <div className="zoho-mockup-card large-mockup bill-estimation-card">
              <img src={require("../../photo/power.png")} alt="Energy Consumption Billing" className="mockup-img" />
              {/* Floating ROI Card */}
              <motion.div 
                animate={{ y: [0, -10, 0] }} 
                transition={{ repeat: Infinity, duration: 4 }} 
                className="roi-floating-badge"
              >
                📉 12% Savings this month
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>













      {/* --- 📜 MODULE 4: HISTORY & STORAGE --- */}
      <section className="portal-section history-section">
        <div className="container grid-2-large grid-2-reverse">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-content">
            <span className="chip-terracotta">BIG DATA STORAGE</span>
            <h2 className="master-display-title"> 
              Secure <span className="accent-orange">Cloud History</span>
            </h2>
            <p className="master-sub-p">
              Protect your portfolio and associated data. Every data point is archived in the secure cloud, 
              allowing for 12-month historical trend analysis and automated PDF auditing.
            </p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, filter: "blur(10px)" }} whileInView={{ opacity: 1, filter: "blur(0px)" }} transition={{ duration: 1 }} className="visual-content">
            <div className="zoho-mockup-card large-mockup">
              <img src={require("../../photo/history_storage.png")} alt="History Storage" className="mockup-img" />
            </div>
          </motion.div>
        </div>
      </section>





                  {/* ================= FINAL CTA SECTION ================= */}

      <section className="energy-final">

        <div className="final-energycontent">

          <h2>
            Take Control of Your Energy Today <br />
            
          </h2>

          <p>Get in touch with our team today!</p>

          <button 
            className="final-energybtn"
            onClick={() => navigate("/enquiry")}
          >
            GET STARTED
          </button>

        </div>

      </section>

      

      {/* ================= FAQ SECTION ================= */}

      <section className="faq-energysection">

        <h2>Frequently asked questions</h2>

        <div className="faq-energycontainer">

          {faqData.map((item, index) => (
            <div 
              key={index} 
              className={`faq-energyitem ${activeIndex === index ? "active" : ""}`}
              onClick={() => toggleFAQ(index)}
            >
              <div className="faq-energyquestion">
                <span>{item.question}</span>
                <span className="arrow">
                  {activeIndex === index ? "−" : "+"}
                </span>
              </div>

              <div className="faq-energyanswer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}

        </div>

      </section>

      

    </div>
  );
}

/* COMPONENT */
function IconPod({ Icon, label, color }) {
  return (
    <motion.div whileHover={{ y: -5 }} className={`pod-source ${color}`}>
      <div className="icon-circle"><Icon size={20} /></div>
      <span className="label-sm">{label}</span>
    </motion.div>
  );
}